import {
 useContext, useEffect, useState, Fragment
} from 'react';
import {
 Link
} from 'react-router-dom';
import styled from 'styled-components';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import {
  channelService, opcodeAssistantService
} from 'shared/api';
import {
 useLocation
} from 'react-router-dom';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from 'shared/components/ui/breadcrumb';
import {
 Context
} from 'shared/context/GlobalState';

// Define extra props for styled component
interface StyledLinkProps {
  $active?: boolean;
}
interface Channel {
  _id: string;
  type: string;
  topic: string;
  // add other fields from API if needed
}

const StyledLink = styled(Link)<StyledLinkProps>`
  text-decoration: none;
  color: ${(props) => (props.$active ? 'black' : '#0d638f')};
  text-transform: capitalize;
  pointer-events: ${(props) => (props.$active ? 'none' : '')};
`;

function DynamicChannelBreadcrumb({
 match
}:any) {

  const location = useLocation();
  const {
 organizations, sub, channelId
} = match.params;
  const [channel, setChannel] = useState<Channel | null>(null);
  const [error, setError] = useState<string | null>(null);

  const type = location.state?.type;
  const topic = location.state?.topic;

  useEffect(() => {
    if (type && topic) return;

    if (!organizations || !sub || !channelId) {
      setError('Missing route params');
      return;
    }
    const fetchChannels = async () => {
      try {
        const response = await channelService.getChannels(organizations, sub);
        const allChannels = response.data;
        // channel by ID
        const ch = allChannels.find((c) => c._id == channelId);
        if (!ch || !ch.type || !ch.topic) throw new Error('Not found');
        setChannel(ch);
      } catch {

        setError('Channel not found');
      }
    };
    fetchChannels();
  }, [organizations, sub, channelId, type, topic]);

  if (type && topic) {
    return (
        <span>
            {type} &gt; {topic}
        </span>
    );
  }
  if (error) return <span>{error}</span>;
  if (!channel) return <span>Loading...</span>;
  return (
      <span>
          {channel.type} &gt; {channel.topic}
      </span>
  );
}

export default function BreadcrumbsCustom({
  maxPaths
}) {
  const {
 organizations
} = useContext(Context) as any;

  const getOrgName = (orgId) => {
    return organizations.find((org) => org._id === orgId)?.name;
  };

  const DynamicOrgBreadcrumb = ({
    match
  }) => (
      <span>{getOrgName(match.params.organizations)}</span>
  );

  const DynamicAssistantBreadcrumb = ({
 match
}: any) => {
    const location = useLocation();
    const [assistantName, setAssistantName] = useState<string | null>(location.state?.title || null);
    const {
 organizations, sub, assistantId
} = match.params;

    useEffect(() => {
      // If we already have the name from state, no need to fetch
      if (location.state?.title) return;

      if (!organizations || !sub || !assistantId) return;

      const fetchAssistant = async () => {
        try {
          const res = await opcodeAssistantService.getOpcodeAssistant(organizations, sub, assistantId);
          setAssistantName(res.data.title || res.data.assistant_id);
        } catch (err) {
          console.error('Failed to fetch assistant title for breadcrumb', err);
        }
      };
      fetchAssistant();
    }, [organizations, sub, assistantId, location.state?.title]);

    return <span>{assistantName || assistantId}</span>;
  };

  const routes = [
    {
      path: '/organizations/:organizations',
      breadcrumb: DynamicOrgBreadcrumb,
    },
    {
      path: '/organizations/:organizations/sub/',
      breadcrumb: null,
    },
    {
      path: '/organizations/:organizations/sub/:sub',
      breadcrumb: null,
    },
    {
      path: '/organizations/:organizations/sub/:sub/assistants/:assistantId',
      breadcrumb: DynamicAssistantBreadcrumb,
    },
    {
      path: '/organizations/:organizations/sub/:sub/knowledge-builder/:collectionId',
      breadcrumb: null,
    },
    // DE Studio
    {
      path: '/organizations/:organizations/sub/:sub/de-studio',
      breadcrumb: 'DE Studio',
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/library',
      breadcrumb: 'Library',
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/library/:channelId',
      breadcrumb: null,
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/channels',
      breadcrumb: 'Library',
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/channels/:channelId',
      breadcrumb: DynamicChannelBreadcrumb,
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/unified-builder',
      breadcrumb: 'Unified Builder',
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/unified-builder/:assistantId',
      breadcrumb: DynamicAssistantBreadcrumb,
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/knowledge',
      breadcrumb: 'Library',
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/knowledge/:collectionId',
      breadcrumb: null,
    },
    {
      path: '/organizations/:organizations/sub/:sub/de-studio/knowledge/:collectionId/:fileName',
      breadcrumb: null,
    },
    // {
    //   path: "/organizations/:organizations/sub/:sub/op-codes/:opCodeName",
    //   breadcrumb: null,
    // },
    {
      path: '/organizations/:organizations/sub/:sub/ai-lab/instructions/:uniqueName/:modelType',
      breadcrumb: null,
    },
    {
      path: '/organizations/:organizations/sub/:sub/channels/:channelId',
      breadcrumb: DynamicChannelBreadcrumb,
    },
  ];

  const breadcrumbs = useBreadcrumbs(routes);

  const renderedBreadcrumbs = maxPaths
    ? breadcrumbs.slice(-maxPaths)
    : breadcrumbs;

  return (
      <Breadcrumb>
          <BreadcrumbList>
              {renderedBreadcrumbs.map(({
 match, breadcrumb
}, index) => {
          const isChannelBreadcrumb = !!match.params?.channelId;
          const isAssistantBreadcrumb = !!match.params?.['bex-assistants'];
          // Use different decoding logic based on type
          // ✅ 1. Declare variable with safe type
          let decodedBreadcrumb: React.ReactNode = breadcrumb;

          // ✅ 2. Extract children safely
          const children = (breadcrumb as any)?.props?.children;

          // ✅ 3. Narrow type before decode
          if (isChannelBreadcrumb || isAssistantBreadcrumb) {
            decodedBreadcrumb =
              typeof children === 'string'
                ? decodeURIComponent(children)
                : breadcrumb;
          } else {
            decodedBreadcrumb =
              typeof children === 'string'
                ? decodeURIComponent(children)
                : breadcrumb;
          }

          // const decodedBreadcrumb = isChannelBreadcrumb || isAssistantBreadcrumb ? (typeof breadcrumb.props?.children === 'string' ? decodeURIComponent(breadcrumb.props.children) : breadcrumb) : decodeURIComponent(breadcrumb.props.children);
          // Redirect specific DE Studio paths to the correct Library tab
          let linkTo = match.pathname;
          if (/\/de-studio\/channels$/.test(match.pathname)) {
            linkTo = match.pathname.replace(/\/de-studio\/channels$/, '/de-studio/library?tab=channels');
          } else if (/\/de-studio\/knowledge$/.test(match.pathname)) {
            linkTo = match.pathname.replace(/\/de-studio\/knowledge$/, '/de-studio/library?tab=collections');
          }

          return (
            breadcrumb && (
            <Fragment key={index}>
                <BreadcrumbItem>
                    {index < renderedBreadcrumbs.length - 1 ? (
                        <StyledLink to={linkTo}>
                            {decodedBreadcrumb}
                        </StyledLink>
                    ) : (
                        <span
                        style={{
                          textTransform: 'capitalize',
                        }}
                      >
                            {decodedBreadcrumb}
                        </span>
                    )}
                </BreadcrumbItem>
                {index < renderedBreadcrumbs.length - 1 && (
                <BreadcrumbSeparator />
                )}
            </Fragment>
            )
          );
        })}
          </BreadcrumbList>
      </Breadcrumb>
  );
}
